import { useState } from "react";
import { Link } from "react-router-dom";
import { GripVertical } from "lucide-react";
import { EVENT_TYPES, ServiceStatusBadge } from "./serviceMeta";
import { eventsInRange, dropDate } from "./scheduleRange";
import { fmtDate } from "@/components/equipment/equipmentMeta";

const LINK = { service: "/service-jobs", hire: "/equipment?tab=orders", return: "/equipment?tab=orders", due: "/equipment?due=soon" };

const BoardCard = ({ e, canDrag, scale }) => {
  const draggable = canDrag && e.type === "service";
  return (
    <Link to={LINK[e.type]} draggable={draggable} data-testid={`board-card-${e.type}-${e.id}`}
      onDragStart={(ev) => { ev.dataTransfer.setData("text/plain", JSON.stringify({ id: e.id, date: e.date })); ev.dataTransfer.effectAllowed = "move"; }}
      className={`block bg-white border border-zinc-200 border-l-4 p-2.5 shadow-sm hover:shadow-md transition-shadow ${EVENT_TYPES[e.type].chip.split(" ").find((c) => c.startsWith("border-l-"))} ${draggable ? "cursor-grab active:cursor-grabbing" : ""} ${e.status === "cancelled" ? "opacity-60" : ""}`}>
      <div className="flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-wider text-zinc-500">
        {draggable && <GripVertical size={12} className="text-zinc-300" />}
        <span className={`w-2 h-2 rounded-full ${EVENT_TYPES[e.type].dot}`} />
        {EVENT_TYPES[e.type].label.replace(/s$/, "")}
        <span className="ml-auto font-mono normal-case text-zinc-600">{scale === "day" ? e.time : `${fmtDate(e.date).slice(0, 6)}${e.time ? ` ${e.time}` : ""}`}</span>
      </div>
      <div className={`text-sm font-bold text-zinc-900 mt-1 break-words ${e.status === "cancelled" ? "line-through" : ""}`}>{e.title}</div>
      <div className="text-xs text-zinc-600 break-words">{e.detail}{e.type === "service" && e.customer ? ` · ${e.customer}` : ""}</div>
      {e.type === "hire" && <div className="text-[11px] text-zinc-400">{fmtDate(e.start)} → {fmtDate(e.end)}</div>}
      {e.type === "service" && <div className="mt-1.5"><ServiceStatusBadge status={e.status} /></div>}
    </Link>
  );
};

const Column = ({ col, items, canDrag, scale, today, onMove }) => {
  const [over, setOver] = useState(false);
  const isNow = col.start <= today && today <= col.end;
  return (
    <div data-testid={`board-col-${col.key}`}
      onDragOver={(ev) => { if (canDrag) { ev.preventDefault(); setOver(true); } }}
      onDragLeave={() => setOver(false)}
      onDrop={(ev) => {
        ev.preventDefault();
        setOver(false);
        try {
          const { id, date } = JSON.parse(ev.dataTransfer.getData("text/plain"));
          const nd = dropDate(scale, col, date);
          if (nd !== date) onMove(id, nd);
        } catch { /* ignore foreign drops */ }
      }}
      className={`flex flex-col ${scale === "day" ? "min-w-[150px]" : "min-w-[240px]"} flex-1 bg-zinc-100 border transition-colors ${over ? "border-brand bg-sky-50" : "border-zinc-200"}`}>
      <div className={`px-3 py-2.5 border-b ${isNow ? "bg-brand text-white border-brand" : "bg-zinc-900 text-white border-zinc-900"}`}>
        <div className="flex items-baseline justify-between gap-2">
          <span className="font-heading font-bold tracking-tight">{col.label}</span>
          <span className="font-mono text-[11px] opacity-70">{items.length}</span>
        </div>
        <div className="text-[11px] opacity-80">{col.sub}</div>
      </div>
      <div className="p-2 space-y-2 flex-1 min-h-[300px] max-h-[65vh] overflow-y-auto">
        {items.map((e) => <BoardCard key={`${e.type}-${e.id}`} e={e} canDrag={canDrag} scale={scale} />)}
        {items.length === 0 && <div className="text-xs text-zinc-400 text-center py-6">Nothing scheduled</div>}
      </div>
    </div>
  );
};

export const ScheduleBoard = ({ columns, events, canDrag, scale, today, onMove }) => (
  <div className="flex gap-3 overflow-x-auto pb-2" data-testid="schedule-board">
    {columns.map((c) => (
      <Column key={c.key} col={c} items={eventsInRange(events, c.start, c.end)} canDrag={canDrag} scale={scale} today={today} onMove={onMove} />
    ))}
  </div>
);
